import {
  DataResidencyConsent,
  useDataResidencyConsent,
} from '@fololabs/design/components';
import { useAuth } from './auth/useAuth';
import { LoginPage } from './pages/LoginPage';
import { LaunchpadPage } from './pages/LaunchpadPage';
import { LoadingState } from './pages/LoadingState';
import { ErrorState } from './pages/ErrorState';
import { DemoProduct } from './pages/DemoProduct';
import { DEMO_MODE } from './config';
import { useHashRoute, productIdFromRoute } from './router';
import { accessibleProducts, productById, productHref } from './products';

export function App() {
  const { state, signIn, signOut, retry, isReal, hostedUi } = useAuth();

  switch (state.status) {
    case 'loading':
      return <LoadingState />;
    case 'error':
      return <ErrorState message={state.message} onRetry={retry} />;
    case 'unauthenticated':
      return (
        <LoginPage onSignIn={signIn} isMock={!isReal} hostedUi={hostedUi} />
      );
    case 'authenticated':
      // Render the signed-in shell as its own component so the consent hook
      // mounts with the real identity as its key.
      return (
        <AuthedShell
          email={state.email}
          groups={state.groups}
          onSignOut={signOut}
        />
      );
  }
}

function AuthedShell({
  email,
  groups,
  onSignOut,
}: {
  email: string;
  groups: string[];
  onSignOut: () => void;
}) {
  const route = useHashRoute();
  const { consented, accept } = useDataResidencyConsent(email);

  // Data-residency consent — the LAST gate before any real data. Shown after
  // sign-in (tied to the identity) and before the launchpad / products.
  if (!consented) {
    return <DataResidencyConsent variant="gate" onAccept={accept} />;
  }

  // Demo mode: route to an in-app product page when the hash matches a
  // product the user can access. Falls back to the launchpad otherwise.
  if (DEMO_MODE) {
    const id = productIdFromRoute(route);
    const product = id ? productById(id) : undefined;
    const canAccess =
      product != null &&
      accessibleProducts(groups).some((p) => p.id === product.id);
    if (product && canAccess) {
      // Once a product gains a live unit, a manual `#/p/{id}` should go to
      // the unit, not the "not deployed yet" placeholder.
      if (product.unitUrl) {
        window.location.replace(productHref(product, true));
        return <LoadingState />;
      }
      return (
        <DemoProduct
          product={product}
          email={email}
          groups={groups}
          onSignOut={onSignOut}
        />
      );
    }
  }

  return <LaunchpadPage email={email} groups={groups} onSignOut={onSignOut} />;
}
